import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import { formatCurrencyText } from '../../utils/chartFormatters';
import { transformForAccountTable } from '../../utils/wealthTracking/chartDataTransformers';
import { useCountry } from '../../CountryContext';
import { getCurrencySymbol } from '../../countryConfig';

const AccountPerformanceTable = ({ currentSnapshot, previousSnapshot, isDark }) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  const [sortField, setSortField] = useState('netChange');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterPurpose, setFilterPurpose] = useState('all');

  // Transform data
  const accountData = transformForAccountTable(currentSnapshot, previousSnapshot);

  // Filter and sort
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...accountData];

    // Apply filter
    if (filterPurpose !== 'all') {
      filtered = filtered.filter(acc => acc.accountPurpose === filterPurpose);
    }

    // Apply sort
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle string fields
      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric fields
      return sortDirection === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });

    return filtered;
  }, [accountData, sortField, sortDirection, filterPurpose]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  // Get unique account purposes for filter
  const accountPurposes = [...new Set(accountData.map(acc => acc.accountPurpose))];

  if (accountData.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        <p>No account data available for this snapshot.</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* Header with filter */}
      <div className={`p-3 sm:p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3`}>
        <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Account Performance
        </h3>

        {accountPurposes.length > 1 && (
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
            <select
              value={filterPurpose}
              onChange={(e) => setFilterPurpose(e.target.value)}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg border text-sm ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="all">All Accounts</option>
              {accountPurposes.map(purpose => (
                <option key={purpose} value={purpose}>
                  {purpose.charAt(0).toUpperCase() + purpose.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Mobile scroll hint */}
      <div className={`px-3 py-2 sm:hidden text-xs text-center ${isDark ? 'bg-gray-900/30 text-gray-400' : 'bg-gray-50 text-gray-600'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        ← Scroll horizontally to see all columns →
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr
              className={`border-b ${
                isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <th
                className={`px-4 py-3 text-left text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('accountName')}
              >
                Account
                <SortIcon field="accountName" />
              </th>
              <th
                className={`px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('openingBalance')}
              >
                Opening
                <SortIcon field="openingBalance" />
              </th>
              <th
                className={`px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('contributions')}
              >
                Contrib.
                <SortIcon field="contributions" />
              </th>
              <th
                className={`px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('marketGrowth')}
              >
                Growth
                <SortIcon field="marketGrowth" />
              </th>
              <th
                className={`px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('withdrawals')}
              >
                Withdraw
                <SortIcon field="withdrawals" />
              </th>
              <th
                className={`px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('closingBalance')}
              >
                Closing
                <SortIcon field="closingBalance" />
              </th>
              <th
                className={`px-4 py-3 text-right text-xs font-semibold cursor-pointer hover:bg-opacity-80 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                onClick={() => handleSort('changePercent')}
              >
                Change
                <SortIcon field="changePercent" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((account, index) => (
              <tr
                key={account.id || index}
                className={`border-b ${
                  isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'
                } transition-colors`}
              >
                {/* Account Name */}
                <td className={`px-4 py-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <div>
                    <p className="font-semibold text-sm">{account.accountName}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {account.accountType}
                    </p>
                  </div>
                </td>

                {/* Opening Balance */}
                <td className={`px-4 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatCurrencyText(account.openingBalance, currencySymbol)}
                </td>

                {/* Contributions */}
                <td className="px-4 py-3 text-right text-sm">
                  {account.contributions !== 0 ? (
                    <span className="text-blue-500 font-semibold">
                      +{formatCurrencyText(account.contributions, currencySymbol)}
                    </span>
                  ) : (
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>-</span>
                  )}
                </td>

                {/* Market Growth */}
                <td className="px-4 py-3 text-right text-sm">
                  {account.marketGrowth !== 0 ? (
                    <span
                      className={`font-semibold ${
                        account.marketGrowth >= 0 ? 'text-green-500' : 'text-red-500'
                      }`}
                    >
                      {account.marketGrowth >= 0 ? '+' : ''}
                      {formatCurrencyText(account.marketGrowth, currencySymbol)}
                    </span>
                  ) : (
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>-</span>
                  )}
                </td>

                {/* Withdrawals */}
                <td className="px-4 py-3 text-right text-sm">
                  {account.withdrawals !== 0 ? (
                    <span className="text-red-500 font-semibold">
                      -{formatCurrencyText(Math.abs(account.withdrawals), currencySymbol)}
                    </span>
                  ) : (
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>-</span>
                  )}
                </td>

                {/* Closing Balance */}
                <td className={`px-4 py-3 text-right text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrencyText(account.closingBalance, currencySymbol)}
                </td>

                {/* Change % */}
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {account.netChange !== 0 && (
                      <>
                        {account.netChange >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        )}
                        <span
                          className={`text-sm font-semibold ${
                            account.netChange >= 0 ? 'text-green-500' : 'text-red-500'
                          }`}
                        >
                          {account.changePercent >= 0 ? '+' : ''}
                          {account.changePercent.toFixed(1)}%
                        </span>
                      </>
                    )}
                    {account.netChange === 0 && (
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        0%
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

          {/* Footer with totals */}
          <tfoot>
            <tr
              className={`font-semibold ${
                isDark ? 'bg-gray-900/50 text-white' : 'bg-gray-50 text-gray-900'
              }`}
            >
              <td className="px-4 py-3 text-sm">Total</td>
              <td className="px-4 py-3 text-right text-sm">
                {formatCurrencyText(
                  filteredAndSortedData.reduce((sum, acc) => sum + acc.openingBalance, 0),
                  currencySymbol
                )}
              </td>
              <td className="px-4 py-3 text-right text-sm text-blue-500">
                +{formatCurrencyText(
                  filteredAndSortedData.reduce((sum, acc) => sum + acc.contributions, 0),
                  currencySymbol
                )}
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {(() => {
                  const totalGrowth = filteredAndSortedData.reduce((sum, acc) => sum + acc.marketGrowth, 0);
                  return (
                    <span className={totalGrowth >= 0 ? 'text-green-500' : 'text-red-500'}>
                      {totalGrowth >= 0 ? '+' : ''}
                      {formatCurrencyText(totalGrowth, currencySymbol)}
                    </span>
                  );
                })()}
              </td>
              <td className="px-4 py-3 text-right text-sm text-red-500">
                -{formatCurrencyText(
                  filteredAndSortedData.reduce((sum, acc) => sum + Math.abs(acc.withdrawals), 0),
                  currencySymbol
                )}
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {formatCurrencyText(
                  filteredAndSortedData.reduce((sum, acc) => sum + acc.closingBalance, 0),
                  currencySymbol
                )}
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {(() => {
                  const totalChange = filteredAndSortedData.reduce((sum, acc) => sum + acc.netChange, 0);
                  const totalOpening = filteredAndSortedData.reduce((sum, acc) => sum + acc.openingBalance, 0);
                  const avgPercent = totalOpening !== 0 ? (totalChange / totalOpening) * 100 : 0;
                  return (
                    <span className={avgPercent >= 0 ? 'text-green-500' : 'text-red-500'}>
                      {avgPercent >= 0 ? '+' : ''}
                      {avgPercent.toFixed(1)}%
                    </span>
                  );
                })()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default AccountPerformanceTable;
